import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { ReuseModal } from "../../ReuseModal";
import { useEffect, useMemo, useState } from "react";
import { ListItem } from "./ListItem";
import { SectionItemType } from "../../../../types/apiTypes";
import { ModalSize } from "../../../../types/enums";

const optionList = ["All sections", "Selected sections: "];

const toggleAtPath = (
  tree: Record<string, SectionItemType>,
  path: string[],
  checked?: boolean | null,
): Record<string, SectionItemType> => {
  if (path.length === 0) return tree;

  const [head, ...rest] = path;
  if (!tree[head]) return tree;

  const node = tree[head];

  if (rest.length === 0) {
    const newChecked =
      checked === null
        ? !node.checked
        : checked === undefined
          ? !node.checked
          : checked;

    const newValue =
      Object.keys(node.value).length > 0
        ? setAllChecked(node.value, newChecked)
        : node.value;

    return {
      ...tree,
      [head]: { ...node, checked: newChecked, value: newValue },
    };
  }

  const updatedChildValue = toggleAtPath(node.value, rest, checked);

  const childrenCheckedValues = Object.values(updatedChildValue).map(
    (c) => c.checked,
  );
  const parentChecked =
    childrenCheckedValues.length > 0
      ? childrenCheckedValues.every(Boolean)
      : node.checked;

  return {
    ...tree,
    [head]: { ...node, value: updatedChildValue, checked: parentChecked },
  };
};

const setAllChecked = (
  tree: Record<string, SectionItemType>,
  checked: boolean,
): Record<string, SectionItemType> => {
  if (!tree) return {};
  // @ts-ignore
  return Object.entries(tree).reduce(
    (acc, [k, v]) => {
      acc[k] = { ...v, checked, value: setAllChecked(v.value, checked) };
      return acc;
    },
    {} as Record<string, SectionItemType>,
  );
};

const countTopLevelChecked = (tree: Record<string, SectionItemType>) => {
  return Object.values(tree).reduce(
    (acc, node) => {
      const entry = Object.entries(node.value);
      if (entry.length > 0) {
        return (
          acc +
          entry.reduce(
            (subAcc, [key, el]) => (el.checked ? subAcc + 1 : subAcc),
            0,
          )
        );
      }

      return node.checked ? acc + 1 : acc;
    },

    0,
  );
};

export const ReuseManageSectionsModal = ({
  title,
  sections = {},
  description,
  includeText,
  isLoading,
  onClose,
  onBack,
  onSubmit,
  submitBtnText = "Save Changes",
  cancelBtnText = "Back",
}: {
  sections?: Record<string, SectionItemType>;
  title: string;
  includeText: string;
  description: string;
  submitBtnText?: string;
  cancelBtnText?: string;
  isLoading: boolean;
  onClose?: () => void;
  onBack?: () => void;
  onSubmit: (sections: Record<string, SectionItemType>) => void;
}) => {
  const [selectedSections, setSelectedSections] =
    useState<Record<string, SectionItemType>>(sections);
  const [option, setOption] = useState<string>(optionList[0]);

  const selectedSectionsAmount = useMemo(
    () => countTopLevelChecked(selectedSections),
    [selectedSections],
  );

  useEffect(() => {
    sections && setSelectedSections(sections);
  }, [sections]);

  useEffect(() => {
    if (option === optionList[0]) {
      setSelectedSections((prev) => setAllChecked(prev, true));
    }
  }, [option]);

  const selectSectionHandler = (sectionKey: string) => {
    setSelectedSections((prev) => toggleAtPath(prev, [sectionKey], null));
  };

  const selectSubSectionHandler = (sectionKey: string, subKey: string) => {
    setSelectedSections((prev) =>
      toggleAtPath(prev, [sectionKey, subKey], null),
    );
  };

  const selectArticleSectionHandler = (
    sectionKey: string,
    subKey: string,
    articleKey: string,
  ) => {
    setSelectedSections((prev) =>
      toggleAtPath(prev, [sectionKey, subKey, articleKey], null),
    );
  };

  const submitHandler = () => {
    onSubmit(selectedSections);
  };

  return (
    <ReuseModal
      isOpen={true}
      onClose={() => onClose && onClose()}
      onCancel={onBack}
      onSubmit={submitHandler}
      title={title}
      maxWidth={ModalSize.small_mid}
      submitBtnText={submitBtnText}
      cancelBtnText={cancelBtnText}
      submitBtnColor="#1C3C6C"
      isDisabled={isLoading || selectedSectionsAmount === 0}
      isDisabledCancelBtn={isLoading}
      isLoading={isLoading}
    >
      <Stack gap="16px" paddingTop={"8px"}>
        <Typography component="span" variant="body1" color="text.secondary">
          {description}
        </Typography>

        <Stack gap="8px">
          <Typography
            fontWeight={500}
            variant="subtitle1"
            color="custom.textDarkBlue"
          >
            Sections to include
          </Typography>

          <FormControl sx={{ padding: "0 12px" }}>
            <RadioGroup
              aria-labelledby="sections-radio"
              value={option}
              name="radio-sections"
              onChange={(e) => setOption(e.target.value)}
              sx={{ flexDirection: "row", gap: "16px" }}
            >
              <FormControlLabel
                value={optionList[0]}
                control={<Radio />}
                label={optionList[0]}
              />
              <FormControlLabel
                value={optionList[1]}
                control={<Radio />}
                label={`${optionList[1]}${selectedSectionsAmount}`}
              />
            </RadioGroup>
          </FormControl>
        </Stack>

        {option === optionList[1] && (
          <Stack gap="8px">
            <Typography
              fontWeight={500}
              variant="subtitle1"
              color="custom.textDarkBlue"
            >
              {includeText}
            </Typography>

            <Stack maxHeight={"320px"} overflow={"auto"}>
              {Object.entries(selectedSections).map(([sectionKey, item]) => (
                <ListItem
                  key={sectionKey}
                  sectionKey={sectionKey}
                  label={item.label}
                  values={item.value}
                  selectSectionHandler={selectSectionHandler}
                  selectSubSectionHandler={selectSubSectionHandler}
                  selectArticleSectionHandler={selectArticleSectionHandler}
                  isChecked={item.checked}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </ReuseModal>
  );
};
